import React from "react";
import styled from "styled-components";

import { FontSize } from "../../../../components/Text";
import { Spacing } from "../../../../theme/Spacing";

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(6rem, 10rem) minmax(0, 1fr) auto;
  align-items: center;
  column-gap: ${Spacing.m};
  padding-block: ${Spacing.s};
  border-bottom: 1px solid ${({ theme }) => theme.sub};

  &:last-child {
    border-bottom: none;
  }
`;

const Label = styled.span`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.textColor};
  opacity: 0.65;
  overflow-wrap: anywhere;
`;

const Value = styled.div`
  min-width: 0;
  font-size: ${FontSize.m};
  color: ${({ theme }) => theme.textColor};
  overflow-wrap: anywhere;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

type EntryDetailsRowProps = {
  label: React.ReactNode;
  /**
   * Which way the value's own text reads. Phone numbers, emails, dates and ids
   * read left to right whatever the page language is - in a right-to-left page
   * a leading "+" would otherwise end up trailing the number and its groups
   * run backwards. `auto` follows the text itself. Only the text is isolated:
   * the cell keeps the page's direction, so every value starts at the same
   * edge as its label.
   */
  dir?: "auto" | "ltr" | "rtl";
  actions?: React.ReactNode;
  dataTestId?: string;
  children: React.ReactNode;
};

export const EntryDetailsRow: React.FC<EntryDetailsRowProps> = ({
  label,
  dir = "auto",
  actions,
  dataTestId,
  children,
}) => (
  <Row data-testid={dataTestId}>
    <Label>{label}</Label>

    <Value>
      <bdi dir={dir}>{children}</bdi>
    </Value>

    {actions ? <Actions>{actions}</Actions> : null}
  </Row>
);
